import { switchMap, take } from 'rxjs/operators';
import moment from 'moment';
import {
  SpacesAction,
  SpacesActionTypes,
  SpacesState,
} from '../../types/spaces';
import {
  ResourceStatus,
  RESOURCE_IDLE,
  RESOURCE_LOADING,
} from '../../types/resource';
import { actions } from '../index';
import { getRequiredSessionToken } from '../../utils/session-token';

import createRxStore, { rxDispatch, ofType } from '..';

import {
  fetchSingleSpace,
  fetchSingleSpaceMPIs,
  fetchSingleSpaceCounts,
} from '../../rx-actions/load-spaces';

export const initialState: SpacesState = {
  resource: RESOURCE_IDLE,
  counts: {
    date: '',
    resource: RESOURCE_IDLE,
  },
  mpis: {
    resource: RESOURCE_IDLE,
  },
};

function getYesterdaysDate(): string {
  return moment().subtract(1, 'day').format('YYYY-MM-DD');
}

function reducer(state: SpacesState, action: SpacesAction): SpacesState {
  switch (action.type) {
    case SpacesActionTypes.SPACES_CHANGE_PAGE_DETAIL:
      return {
        ...state,
        resource: RESOURCE_IDLE,
        counts: { ...initialState.counts, date: getYesterdaysDate() },
      };

    case SpacesActionTypes.SPACES_LOAD_DETAIL_COMPLETE:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            space: action.space,
          },
        },
        counts: state.counts,
      };

    case SpacesActionTypes.SPACES_LOAD_START:
      return {
        ...state,
        resource: RESOURCE_LOADING,
      };
    case SpacesActionTypes.SPACES_LOAD_ERROR:
      return {
        ...state,
        resource: { status: ResourceStatus.ERROR, error: action.error },
      };
    case SpacesActionTypes.SPACES_DETAIL_COUNTS_CHANGE_DATE:
      return {
        ...state,
        counts: {
          date: action.date,
          resource: RESOURCE_IDLE,
        },
      };
    case SpacesActionTypes.SPACES_DETAIL_COUNTS_LOAD_START:
      return {
        ...state,
        counts: {
          ...state.counts,
          resource: RESOURCE_LOADING,
        },
      };
    case SpacesActionTypes.SPACES_DETAIL_COUNTS_LOAD_ERROR:
      return {
        ...state,
        counts: {
          ...state.counts,
          resource: { status: ResourceStatus.ERROR, error: action.error },
        },
      };
    case SpacesActionTypes.SPACES_DETAIL_COUNTS_LOAD_COMPLETE:
      return {
        ...state,
        counts: {
          ...state.counts,
          resource: { status: ResourceStatus.COMPLETE, data: action.counts },
        },
      };
    case SpacesActionTypes.SPACE_DETAIL_MPIS_LOAD_ERROR:
      return {
        ...state,
        mpis: {
          ...state.mpis,
          resource: { status: ResourceStatus.ERROR, error: action.error },
        },
      };
    case SpacesActionTypes.SPACE_DETAIL_MPIS_LOAD_START:
      return {
        ...state,
        mpis: {
          ...state.mpis,
          resource: RESOURCE_LOADING,
        },
      };
    case SpacesActionTypes.SPACE_DETAIL_MPIS_LOAD_COMPLETE:
      return {
        ...state,
        mpis: {
          ...state.mpis,
          resource: { status: ResourceStatus.COMPLETE, data: action.mpis },
        },
      };
    default:
      return state;
  }
}

const SpacesStore = createRxStore('SpacesStore', initialState, reducer);

// =================================
// SIDE EFFECT: on initial load of the detail view, figure out what request needs to be made and make it to
// load the data.
// =================================
actions
  .pipe(
    ofType(SpacesActionTypes.SPACES_CHANGE_PAGE_DETAIL),
    switchMap(
      () => SpacesStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();

    fetchSingleSpace(rxDispatch, state, jwtToken, (action as any).id).then(
      (space) => {
        fetchSingleSpaceCounts(rxDispatch, space, jwtToken, state.counts.date);
        fetchSingleSpaceMPIs(rxDispatch, space, jwtToken);
      },
    );
  });

// =================================
// SIDE EFFECT: when the date selected for the unbounded chart is changed, refetch the count data.
// =================================
actions
  .pipe(
    ofType(SpacesActionTypes.SPACES_DETAIL_COUNTS_CHANGE_DATE),
    switchMap(
      () => SpacesStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();

    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }

    fetchSingleSpaceCounts(
      rxDispatch,
      state.resource.data.space,
      jwtToken,
      state.counts.date,
    );
  });

export default SpacesStore;
